import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Eye,
  Users,
  MousePointerClick,
  Share2,
  BarChart3,
  Loader2,
  TrendingUp,
  Store,
  EyeOff,
  Smartphone,
  RefreshCw,
} from "lucide-react";
import { resolveAssetUrl } from "@/lib/assetUrl";
import { isExcludedFromStats, setExcludeFromStats } from "@/lib/analytics";

/**
 * AdminTrafficTab — site-wide traffic dashboard.
 *
 * Backed by GET /api/admin/analytics/traffic?days=N.
 * Complements Google Analytics 4 (which handles referrers/geo/device) with an
 * in-app pulse: page views, unique sessions, top pages, top businesses.
 */
export default function AdminTrafficTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [includeAdmin, setIncludeAdmin] = useState(false);
  const [deviceExcluded, setDeviceExcluded] = useState(isExcludedFromStats());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/analytics/traffic`, {
          params: { days, include_admin: includeAdmin },
          withCredentials: true,
        });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Couldn't load traffic");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, includeAdmin]);

  const toggleDeviceExclude = () => {
    const next = !deviceExcluded;
    setExcludeFromStats(next);
    setDeviceExcluded(next);
    toast.success(
      next
        ? "This device won't be counted in stats going forward."
        : "This device will now be counted in stats again."
    );
  };

  const [recomputing, setRecomputing] = useState(false);
  const recomputeAdminVisits = async () => {
    setRecomputing(true);
    try {
      const currentSessionId = (() => {
        try { return localStorage.getItem("tbc_session_id") || null; }
        catch { return null; }
      })();
      const r = await axios.post(
        `${API}/admin/analytics/recompute-admin-visits`,
        { session_ids: currentSessionId ? [currentSessionId] : [] },
        { withCredentials: true }
      );
      toast.success(
        `Back-filled ${r.data.flagged} events across ${r.data.sessions_used.length} admin sessions. ${r.data.total_admin_events} events total now flagged as admin.`
      );
      // Refresh the current view so the numbers reflect the new flagging
      const r2 = await axios.get(`${API}/admin/analytics/traffic`, {
        params: { days, include_admin: includeAdmin },
        withCredentials: true,
      });
      setData(r2.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recompute failed");
    } finally {
      setRecomputing(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="p-12 flex items-center justify-center text-gray-500 font-body">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading site traffic…
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals || {};
  const cards = [
    { label: "Page views", value: t.page_views, icon: Eye, color: "text-tbc-gold" },
    { label: "Unique sessions", value: t.unique_sessions, icon: Users, color: "text-emerald-600" },
    { label: "Business profile views", value: t.profile_views, icon: Store, color: "text-tbc-navy" },
    { label: "Contact clicks", value: t.contact_clicks, icon: MousePointerClick, color: "text-tbc-coral" },
    { label: "Product views", value: t.product_views, icon: BarChart3, color: "text-sky-600" },
    { label: "Share clicks", value: t.share_clicks, icon: Share2, color: "text-purple-600" },
  ];

  const timeseries = data.timeseries || [];
  const maxViews = Math.max(1, ...timeseries.map((p) => p.views));

  return (
    <div className="space-y-6">
      {/* Range selector + exclude toggles */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-tbc-gold" /> Site Traffic
          </h2>
          <p className="text-sm text-gray-500 font-body mt-1">
            Last {days} days · {includeAdmin ? "including your visits" : "your visits excluded"} · complements Google Analytics 4
          </p>
        </div>
        <div className="flex gap-1 bg-white border border-gray-200 rounded-full p-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                days === d ? "bg-tbc-navy text-white" : "text-gray-500 hover:text-tbc-navy"
              }`}
              data-testid={`traffic-range-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Exclude-from-stats controls */}
      <div className="bg-tbc-navy/5 border border-tbc-navy/10 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
        <div className="flex-1">
          <p className="font-heading text-sm text-tbc-navy flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-tbc-navy" /> Filter out your own traffic
          </p>
          <p className="text-xs text-gray-600 font-body mt-0.5">
            Admin visits (yours + team) are hidden from the numbers by default. Toggle to include, or mark THIS device to be excluded even when signed out.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setIncludeAdmin(!includeAdmin)}
            className={`text-xs px-3 py-2 rounded-full border transition-colors ${
              includeAdmin
                ? "bg-tbc-navy text-white border-tbc-navy"
                : "bg-white text-tbc-navy border-tbc-navy/30 hover:bg-tbc-navy/5"
            }`}
            data-testid="traffic-include-admin-toggle"
          >
            {includeAdmin ? "Showing my visits" : "Hide my visits"}
          </button>
          <button
            onClick={toggleDeviceExclude}
            className={`text-xs px-3 py-2 rounded-full border transition-colors flex items-center gap-1.5 ${
              deviceExcluded
                ? "bg-tbc-gold text-tbc-navy border-tbc-gold"
                : "bg-white text-tbc-navy border-tbc-navy/30 hover:bg-tbc-navy/5"
            }`}
            data-testid="traffic-device-exclude-toggle"
          >
            <EyeOff className="w-3 h-3" />
            {deviceExcluded ? "This device excluded" : "Exclude this device"}
          </button>
          <button
            onClick={recomputeAdminVisits}
            disabled={recomputing}
            className="text-xs px-3 py-2 rounded-full border border-tbc-navy/30 bg-white text-tbc-navy hover:bg-tbc-navy/5 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            data-testid="traffic-recompute-admin-visits"
            title="Scan historical events and back-fill admin visits for the sessions we know are yours"
          >
            {recomputing ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
            Recompute historical
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="traffic-stat-cards">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <c.icon className={`w-4 h-4 ${c.color}`} />
              <span className="text-xs text-gray-500 font-body">{c.label}</span>
            </div>
            <div className="font-heading text-2xl text-tbc-navy">
              {(c.value ?? 0).toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {/* Timeseries bar chart (CSS-only, no chart lib dep) */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <p className="text-xs uppercase tracking-widest text-gray-500 font-body mb-3">
          Daily page views
        </p>
        <div className="flex items-end gap-1 h-32">
          {timeseries.map((p) => (
            <div
              key={p.date}
              title={`${p.date}: ${p.views} views · ${p.unique_sessions} sessions`}
              className="flex-1 bg-tbc-gold/80 rounded-t hover:bg-tbc-gold transition-colors cursor-pointer"
              style={{ height: `${Math.max(2, (p.views / maxViews) * 100)}%` }}
              data-testid={`traffic-bar-${p.date}`}
            />
          ))}
        </div>
        <div className="flex justify-between mt-2 text-[10px] text-gray-400 font-body">
          <span>{timeseries[0]?.date}</span>
          <span>{timeseries[timeseries.length - 1]?.date}</span>
        </div>
      </div>

      {/* Top pages + Top businesses side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <p className="text-xs uppercase tracking-widest text-gray-500 font-body mb-3">
            Top pages
          </p>
          {(data.top_pages || []).length === 0 ? (
            <p className="text-sm text-gray-400 font-body">No page views yet.</p>
          ) : (
            <ul className="space-y-2">
              {data.top_pages.map((p) => (
                <li
                  key={p.page}
                  className="flex justify-between items-center text-sm font-body"
                  data-testid={`top-page-${p.page.replace(/\W+/g, "-")}`}
                >
                  <span className="text-tbc-navy truncate mr-2">{p.page}</span>
                  <span className="text-tbc-gold font-medium">{p.views.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <p className="text-xs uppercase tracking-widest text-gray-500 font-body mb-3">
            Top businesses (by profile views)
          </p>
          {(data.top_businesses || []).length === 0 ? (
            <p className="text-sm text-gray-400 font-body">No profile views yet.</p>
          ) : (
            <ul className="space-y-3">
              {data.top_businesses.map((b) => (
                <li
                  key={b.business_id}
                  className="flex items-center gap-3"
                  data-testid={`top-biz-${b.business_id}`}
                >
                  {b.logo_url ? (
                    <img
                      src={resolveAssetUrl(b.logo_url)}
                      alt={b.name}
                      className="w-8 h-8 rounded object-cover flex-shrink-0"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded bg-tbc-cream flex items-center justify-center flex-shrink-0">
                      <Store className="w-4 h-4 text-tbc-gold/60" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-tbc-navy font-medium truncate">{b.name}</p>
                    <p className="text-xs text-gray-400 font-body">{b.category || "—"}</p>
                  </div>
                  <span className="text-tbc-gold font-medium text-sm">{b.views}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
